import {
  CellClassParams,
  CellClickedEvent,
  ColDef,
  HeaderClassParams,
  RowClassParams,
  ValueFormatterFunc,
  ValueFormatterParams,
  ValueParserFunc,
} from '@ag-grid-community/core';
import { ChartDataset } from 'chart.js';
import { BehaviorSubject } from 'rxjs';

import { CanvasComponentOptions } from '@shared/components/canvas-chart/canvas-chart.component';
import { TableConstants } from '@shared/constants/table.constants';
import { decimalRoundingToString } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { AgMonthCellComponent } from '../../../../components/ag-month-cell/ag-month-cell.component';
import { TotalDriverSiteDistribution } from '../forecast-site-curves.component';

type Timeline = BehaviorSubject<{ endDate?: string; startDate?: string }>;
export class SiteCurvesConstants {
  static readonly GRID_OPTIONS = {
    onCellClicked(event: CellClickedEvent) {
      const cellRange = event.api.getCellRanges();

      if ((cellRange?.length || 0) > 1) {
        event.api.stopEditing();
      }
    },
    ...TableConstants.DEFAULT_GRID_OPTIONS.EDIT_GRID_OPTIONS,
    getRowStyle: (params: RowClassParams) => {
      if (params.node.rowPinned) {
        return { 'font-weight': 'bold', 'justify-item': 'end' };
      }

      return {};
    },
    groupIncludeFooter: true,
    headerHeight: 40,
    pinnedBottomRowData: [],
    rowClassRules: {
      'has-error': (params: RowClassParams) => params.data.showError,
    },
  };
  static readonly GRID_OPTIONS_EXEL_STYLES = [
    {
      font: { bold: true, color: '#FFFFFF', fontName: 'Arial', size: 11 },
      id: 'header',
      interior: { color: '#094673', pattern: 'Solid', patternColor: '#094673' },
    },
    {
      font: { color: '#D73C37' },
      id: 'text-aux-error',
    },
    {
      font: { color: '#437F7F' },
      id: 'text-aux-green',
    },
    {
      alignment: { horizontal: 'Right' },
      font: { fontName: 'Arial', size: 11 },
      id: 'cellPercent',
      numberFormat: { format: '0.00;-0.00;—;—' },
    },
    {
      font: { bold: true, color: '#FFFFFF', fontName: 'Arial', size: 11 },
      id: 'first_row',
      interior: { color: '#999999', pattern: 'Solid', patternColor: '#999999' },
    },
    {
      font: { bold: true, color: '#000000', fontName: 'Arial', size: 11 },
      id: 'total_row_header',
      interior: { color: '#D9D9D9', pattern: 'Solid', patternColor: '#D9D9D9' },
    },
    {
      dataType: 'Number',
      font: { bold: true, color: '#000000', fontName: 'Arial', size: 11 },
      id: 'total_row',
      interior: { color: '#D9D9D9', pattern: 'Solid', patternColor: '#D9D9D9' },
      numberFormat: { format: '#,##0.00' },
    },
    {
      dataType: 'Number',
      font: { bold: true, color: '#000000', fontName: 'Arial', size: 11 },
      id: 'total_row_extended_decimal',
      interior: { color: '#D9D9D9', pattern: 'Solid', patternColor: '#D9D9D9' },
      numberFormat: { format: '#,##0.00######' },
    },
    {
      alignment: {
        horizontal: 'Right',
      },
      dataType: 'String',
      font: { bold: true, color: '#000000', fontName: 'Arial', size: 11 },
      id: 'total_row_percent',
      interior: { color: '#D9D9D9', pattern: 'Solid', patternColor: '#D9D9D9' },
    },
  ];

  // eslint-disable-next-line max-lines-per-function
  static columnDefs(
    valueFormatter: ValueFormatterFunc,
    valueParser: ValueParserFunc,
    editCell: boolean,
    timeline$: Timeline
  ): ColDef[] {
    return [
      {
        cellRenderer: AgMonthCellComponent,
        cellRendererParams: {
          noMonthInTimelineTooltipMessage:
            'This month is no longer included in the trial timeline. Please adjust your Site Curve or Trial Timeline to fix this issue.',
          timeline$,
        },
        editable: false,
        field: 'distribution_month',
        headerName: 'Month',
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === 'TOTAL') {
            return 'TOTAL';
          }

          return params.value
            ? Utils.dateFormatter(params.value, { day: undefined, year: '2-digit' })
            : '';
        },
      },
      {
        aggFunc: 'sum',
        cellDataType: 'text',
        field: 'sites_activated',
        headerName: 'Sites Activated',
        valueFormatter: valueFormatter,
        valueParser: valueParser,
      },
      {
        aggFunc: 'sum',
        cellDataType: 'text',
        field: 'sites_closed',
        headerName: 'Sites Closed',
        valueFormatter: valueFormatter,
        valueParser: valueParser,
      },
      {
        aggFunc: 'sum',
        editable: false,
        field: 'net_sites_per_month',
        headerName: 'Net Sites Per Month',
        valueFormatter: valueFormatter,
      },
      {
        aggFunc: 'sum',
        cellClass: () => {
          return editCell
            ? ['opacity-70', 'cellPercent', 'justify-end']
            : ['justify-end', 'cellPercent'];
        },
        editable: false,
        field: 'net_sites_per_month_percentage',
        headerName: 'Net Sites Per Month %',
        valueFormatter: (val: ValueFormatterParams) => {
          return val.value ? `${decimalRoundingToString(val.value, 2)}%` : Utils.zeroHyphen;
        },
      },
      {
        aggFunc: 'sum',
        editable: false,
        field: 'total_sites_activated',
        headerName: 'Total Sites Activated',
        valueFormatter: valueFormatter,
      },
      {
        aggFunc: 'sum',
        cellClass: () => {
          return editCell
            ? ['opacity-70', 'cellPercent', 'justify-end']
            : ['justify-end', 'cellPercent'];
        },
        editable: false,
        field: 'sites_activated_percentage',
        headerName: 'Sites Activated %',
        valueFormatter: (val: ValueFormatterParams) => {
          return val.value ? `${decimalRoundingToString(val.value, 2)}%` : Utils.zeroHyphen;
        },
      },
      {
        aggFunc: 'sum',
        cellClass: () => {
          return editCell
            ? ['opacity-70', 'cellPercent', 'justify-end']
            : ['justify-end', 'cellPercent'];
        },
        editable: false,
        field: 'sites_closed_percentage',
        headerName: 'Sites Closed %',
        valueFormatter: (val: ValueFormatterParams) => {
          return val.value ? `${decimalRoundingToString(val.value, 2)}%` : Utils.zeroHyphen;
        },
      },
    ];
  }

  static getCellClasses =
    (editMode$: BehaviorSubject<boolean>) =>
    (params: CellClassParams): string | string[] => {
      const editCell = editMode$.getValue();

      if (params.colDef.field === 'distribution_month') {
        return editCell ? ['opacity-70', 'justify-start'] : 'justify-start';
      }

      if (params.colDef.field === 'sites_activated' || params.colDef.field === 'sites_closed') {
        return editCell ? ['editable-cell', 'justify-end'] : 'justify-end';
      }

      if (params.colDef.field === 'net_sites_per_month') {
        if (Utils.isNegativeAndNotCloseEnoughToZero(params.value)) {
          return 'justify-end';
        }

        return editCell ? ['opacity-70', 'justify-end'] : 'justify-end';
      }

      return 'justify-end';
    };

  static getHeaderClasses =
    (editMode$: BehaviorSubject<boolean>) =>
    (params: HeaderClassParams): string | string[] => {
      const editCell = editMode$.getValue();

      if (!params.column?.getColId()) {
        return '';
      }

      if (
        params.column.getColId() === 'sites_activated' ||
        params.column.getColId() === 'sites_closed'
      ) {
        return editCell
          ? ['ag-header-edit-mode', 'ag-header-align-center']
          : ['ag-header-align-center'];
      }

      return ['ag-header-align-center'];
    };

  // eslint-disable-next-line max-lines-per-function
  static multiChartOptions(
    data: TotalDriverSiteDistribution[],
    labels: string[]
  ): CanvasComponentOptions {
    const colors = [
      {
        border: true,
        color: '#E3B506',
      },
      {
        border: true,
        color: '#236262',
      },
      {
        border: false,
        color: '#095b95',
      },
      {
        border: false,
        color: '#6B9DBF',
      },
      {
        border: false,
        color: '#BACAD0',
      },
    ].map(({ border, color }) => {
      return <ChartDataset<'line'>>{
        backgroundColor: color,
        borderColor: color,
        borderWidth: border ? 3 : 0,
        hoverBackgroundColor: color,
        hoverBorderColor: color,
        pointBackgroundColor: color,
        pointBorderColor: color,
        pointHitRadius: 4,
        pointHoverBackgroundColor: color,
        pointHoverBorderColor: color,
        pointHoverBorderWidth: 4,
        pointHoverRadius: 4,
        pointRadius: 0,
        pointStyle: 'rectRounded',
      };
    });

    return {
      data: {
        datasets: [
          {
            ...colors[0],
            data: data.map((x) => x.net_sites_per_month || 0),
            label: 'Sites Per Month',
            type: 'line',
          },
          {
            ...colors[1],
            data: data.map((x) => x.total_sites_activated || 0),
            label: 'Total Sites Activated',
            type: 'line',
          },
          {
            ...colors[2],
            data: data.map((x) => x.sites_activated || 0),
            label: 'Sites Activated',
            // https://github.com/chartjs/Chart.js/issues/11356
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            type: 'bar',
          },
          {
            ...colors[3],
            data: data.map((x) => x.sites_closed || 0),
            label: 'Sites Closed',
            // https://github.com/chartjs/Chart.js/issues/11356
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            type: 'bar',
          },
        ],
        labels,
      },
      legend: { bottom: true, left: false, right: false, top: false },
      options: {
        maintainAspectRatio: false,
      },
      type: 'line',
    };
  }
}
